import { Button, Collapse, Colors, Icon, Intent } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'
import * as React from 'react'
import { useSelector } from 'react-redux'
import cable from '../../../../cable'
import Client from '../../../../client'
import { AppState } from '../../../../store'
import { UprightStyle } from '../../../../types'
import { hexToRGBA, rgbaForIntent } from '../../../../utils/color'
import { MarketAccountLite } from '../../v2/types'
import { ConcurrentListingControls } from './components/ConcurrentListingControls'
import { FutureStepSection } from './components/FutureStepSection'
import { MoveDialog } from './components/MoveDialog'
import { PastListingSection } from './components/PastListingSection'
import { SingularListingControls } from './components/SingularListingControls'
import './style.scss'
import { Listing, ProjectedStep, ShopifyMarketAccount } from './types'
import { attentionRequiredOnFutureSteps } from './utils'
import { ProductV4 } from '../../../../types/api'

const styles: UprightStyle = {
  arrow: {
    margin: '15px 0'
  },
  currentStepCallout: {
    padding: 15
  },
  channelName: {
    marginBottom: 4
  },
  futureStepContainer: { display: 'flex', alignItems: 'center' },
  hideStepsBtn: {
    margin: '10px 2px 10px 0px'
  },
  attnRequired: { color: Colors.RED3, margin: `0px 0px 0px 6px` },
  futureStepError: { cursor: 'pointer', height: 30, display: 'flex', alignItems: 'center' },
  spaceBetween: {
    justifyContent: 'space-between'
  },
  stepCard: {
    padding: 0,
    textAlign: 'left'
  },
  futureSteps: {
    background: hexToRGBA(Colors.GRAY3, 0.15),
    padding: '14px 12px 0 12px',
    border: `4px solid ${rgbaForIntent(Intent.PRIMARY, 0.15)}`,
    borderBottom: null,
    borderRadius: '5px 5px 0 0',
    width: '97%',
    margin: '0 auto'
  },
  pastListings: {
    background: hexToRGBA(Colors.GRAY3, 0.15),
    padding: '0 12px 14px 12px',
    border: `4px solid ${rgbaForIntent(Intent.PRIMARY, 0.15)}`,
    borderTop: null,
    borderRadius: '0 0 5px 5px',
    width: '97%',
    margin: '0 auto'
  },
  channelCards: {
    textAlign: 'center'
  }
}

interface Props {
  product: ProductV4
  productPurgedAt?: Date
  productRecycledAt?: Date
  futureSteps?: ProjectedStep[]
  activeListings?: Listing[]
  canonListings: Listing[]
  pastListings?: Listing[]
  shopifyMarketAccounts?: ShopifyMarketAccount[]
  supplier?: { marketAccounts: MarketAccountLite[] }
  concurrent?: boolean
  reload: (setLoadingIndicator?: boolean) => void
  openCreateListingWhichAccountDialog: (marketId: number, additionalQueryParams: {}) => void
  variantsPriceRange?: number[]
}

export const ListingHistorySection: React.FC<Props> = props => {
  const [showFutureSteps, setShowFutureSteps] = React.useState(false)
  const [showPastListings, setShowPastListings] = React.useState(false)
  const [selectedMoveChannelId, setSelectedMoveChannelId] = React.useState(null)
  const [selectedMarketAccountId, setSelectedMarketAccountId] = React.useState(null)

  const organization = useSelector((s: AppState) => s.current.organization)
  const productId = props.product.id

  React.useEffect(() => {
    const connections: ActionCable.Channel[] = props.canonListings?.map(listing => {
      const c = cable.subscriptions.create(
        { channel: 'ListingUpdateChannel', channel_id: listing.marketId, organization_id: organization.id },
        {
          received: data => {
            if (data?.product_id === productId) props.reload(false)
          }
        }
      )
      return c
    })

    return () => {
      connections.map((c: ActionCable.Channel) => c.unsubscribe())
    }
    // Don't want to risk a regression here by calling this hook too much or not at all, disabling the lint for now.
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <MoveDialog
        channelId={selectedMoveChannelId}
        onClose={() => {
          setSelectedMoveChannelId(null)
          setSelectedMarketAccountId(null)
        }}
        // TODO(shopify): - When we allow multiple shops, will need to revisit this
        marketAccountId={selectedMarketAccountId}
        onSubmit={async (channelId, form, callback) => {
          try {
            await Client.v5ProductMove(productId, channelId, form)
            props.reload()
            callback()
          } catch (error) {
            callback(error.response)
          }
        }}
        product={props.product}
      />

      <div className="listing-details inverted">
        <div className="drawer-header" style={styles.spaceBetween}>
          <div>Listings</div>
        </div>
        {props.futureSteps?.length > 0 && (
          <div style={styles.futureStepContainer}>
            <Button
              onClick={() => setShowFutureSteps(!showFutureSteps)}
              style={styles.hideStepsBtn}
              intent={Intent.PRIMARY}
              minimal
              icon={showFutureSteps ? IconNames.CHEVRON_DOWN : IconNames.CHEVRON_RIGHT}
              text={`${showFutureSteps ? 'Hide' : 'Show'} future steps (${props.futureSteps.length})`}
            />
            {attentionRequiredOnFutureSteps(props.futureSteps) && (
              <div
                style={styles.futureStepError}
                onClick={() => setShowFutureSteps(!showFutureSteps)}
                role="button"
                tabIndex={0}
              >
                <Icon icon={IconNames.ERROR} color={Colors.RED3} />
                <p style={styles.attnRequired}>Attention Required</p>
              </div>
            )}
          </div>
        )}
        <div style={styles.channelCards}>
          <Collapse isOpen={showFutureSteps}>
            <FutureStepSection futureSteps={props.futureSteps} />
          </Collapse>

          {props.concurrent && (
            <ConcurrentListingControls
              reload={props.reload}
              productId={productId}
              supplier={props.supplier}
              canonListings={props.canonListings}
              openCreateListingWhichAccountDialog={props.openCreateListingWhichAccountDialog}
            />
          )}
          {!props.concurrent && (
            <SingularListingControls
              product={props.product}
              canonListings={props.canonListings}
              activeListings={props.activeListings}
              variantsPriceRange={props.variantsPriceRange}
              shopifyMarketAccounts={props.shopifyMarketAccounts}
              supplier={props.supplier}
              productPurged={!!props.productPurgedAt}
              productRecycled={!!props.productRecycledAt}
              reload={props.reload}
              onMove={(marketId, marketAccountId) => {
                setSelectedMoveChannelId(marketId)
                setSelectedMarketAccountId(marketAccountId)
              }}
              openCreateListingWhichAccountDialog={props.openCreateListingWhichAccountDialog}
            />
          )}
          <Collapse isOpen={showPastListings}>
            <PastListingSection pastListings={props.pastListings} />
          </Collapse>
        </div>
        {props.pastListings?.length > 0 && (
          <Button
            onClick={() => setShowPastListings(!showPastListings)}
            style={styles.hideStepsBtn}
            intent={Intent.PRIMARY}
            minimal
            icon={showPastListings ? IconNames.CHEVRON_UP : IconNames.CHEVRON_RIGHT}
            text={`${showPastListings ? 'Hide' : 'Show'} past listings (${props.pastListings.length})`}
          />
        )}
      </div>
    </>
  )
}
